import { useEffect, useState } from 'react'
import { AlertCircle, Clock, Loader2, RotateCw } from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import { cn, parseUTCDate } from '@/lib/utils'
import { useRetryAnalysis } from '@/features/communications/hooks'
import type { MessageAnalysis } from '@/features/communications/types'

/** Unfinished analysis states that mean the AI still has work to do on a message. */
function isAnalysisActive(analysis: MessageAnalysis | undefined) {
  return analysis?.state === 'queued' || analysis?.state === 'running'
}

function useElapsed(since: string, ticking: boolean) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!ticking) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [ticking])
  const seconds = Math.max(0, Math.floor((now - parseUTCDate(since).getTime()) / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${String(seconds % 60).padStart(2, '0')}s`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

/** When the next automatic re-run happens: a time today, "tomorrow", or a date further out. */
function formatRetryAt(dateStr: string) {
  const at = parseUTCDate(dateStr)
  const time = at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  const days = Math.round(
    (new Date(at).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86_400_000,
  )
  if (days <= 0) return time
  if (days === 1) return `tomorrow ${time}`
  return `${at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${time}`
}

/** Sweeping band along the bottom edge of a message while the AI works on it. */
export function AnalysisSweep({ analysis }: { analysis: MessageAnalysis | undefined }) {
  if (analysis?.state !== 'running') return null
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 overflow-hidden bg-violet-500/10"
    >
      <span className="block h-full w-2/5 bg-gradient-to-r from-transparent via-violet-500 to-transparent motion-safe:animate-analysis-sweep" />
    </span>
  )
}

/** One line saying where a message's AI analysis stands. */
export function AnalysisStatusLine({
  analysis,
  className,
}: {
  analysis: MessageAnalysis
  className?: string
}) {
  const running = analysis.state === 'running'
  const elapsed = useElapsed(
    running ? analysis.updated_at : analysis.queued_at,
    isAnalysisActive(analysis),
  )
  const content = {
    queued: {
      icon: <Clock className="h-3.5 w-3.5" />,
      text: <>Waiting for AI analysis · {elapsed}</>,
      tone: 'text-violet-700 dark:text-violet-300',
    },
    running: {
      icon: <Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" />,
      text: <>AI is analyzing this email · {elapsed}</>,
      tone: 'text-violet-700 dark:text-violet-300',
    },
    retrying: {
      icon: <RotateCw className="h-3.5 w-3.5" />,
      text: (
        <>
          AI couldn't finish · retry {analysis.retries} of {analysis.max_retries}
          {analysis.retry_at && <> at {formatRetryAt(analysis.retry_at)}</>}
        </>
      ),
      tone: 'text-amber-700 dark:text-amber-300',
    },
    failed: {
      icon: <AlertCircle className="h-3.5 w-3.5" />,
      text: <>AI couldn't process this email</>,
      tone: 'text-destructive',
    },
  }[analysis.state]
  const canRetry = analysis.state === 'retrying' || analysis.state === 'failed'

  return (
    <p
      role="status"
      data-testid="message-analysis-status"
      data-state={analysis.state}
      className={cn('flex items-center gap-1.5 text-xs font-medium', content.tone, className)}
    >
      <span aria-hidden className="shrink-0">
        {content.icon}
      </span>
      <span className="tabular-nums">{content.text}</span>
      {canRetry && <RetryNowButton messageId={analysis.message_id} />}
    </p>
  )
}

function RetryNowButton({ messageId }: { messageId: number }) {
  const retry = useRetryAnalysis()
  return (
    <LoadingButton
      type="button"
      variant="ghost"
      size="sm"
      loading={retry.isPending}
      loadingText="Retrying…"
      onClick={() => retry.mutate(messageId)}
      className="-my-1 h-6 px-2 text-xs font-medium text-foreground"
    >
      Retry now
    </LoadingButton>
  )
}

/** Compact marker on a thread row: the AI is working on (or gave up on) a message inside. */
export function ThreadAnalysisPill({ analyses }: { analyses: MessageAnalysis[] }) {
  const running = analyses.some((a) => a.state === 'running')
  const queued = analyses.some((a) => a.state === 'queued')
  const failed = analyses.some((a) => a.state === 'failed')
  const retrying = analyses.some((a) => a.state === 'retrying')
  if (!running && !queued && !failed && !retrying) return null
  const label = running
    ? 'Analyzing'
    : queued
      ? 'Waiting for AI'
      : failed
        ? 'AI analysis failed'
        : 'AI retrying'
  return (
    <span
      data-testid="thread-analysis-pill"
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
        running || queued
          ? 'bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-200'
          : 'bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200',
      )}
    >
      {running ? (
        <Loader2 aria-hidden className="h-3 w-3 motion-safe:animate-spin" />
      ) : queued ? (
        <Clock aria-hidden className="h-3 w-3" />
      ) : (
        <AlertCircle aria-hidden className="h-3 w-3" />
      )}
      {label}
    </span>
  )
}
